import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, SettingIcon } from '@repo/ui/icons'
import { useRef } from 'react'

import { API } from '../../../api/apis.config'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import { Paths } from '../../../lib/constants/path'
import { promoCodeApplicationMapping } from '../../../lib/types/enums/promo-code/promo-code-application.enum'
import { promoCodeTypeMapping } from '../../../lib/types/enums/promo-code/promo-code-type.enum'
import { PromoCode } from '../../../lib/types/models/promo-code/promo-code'

const PromoCodeListPage = () => {
  const tableRef = useRef<DynamicTableRef<PromoCode>>(null)
  const { navigate, navigateWithEvent } = useNavigator()

  const columns: Column<PromoCode>[] = [
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'code',
      header: 'Code',
      enableSorting: true
    },
    {
      accessorKey: 'discount',
      header: 'Remise',
      enableSorting: true,
      cell: (data) =>
        `${data.row.original.discount} ${promoCodeTypeMapping[data.row.original.type].symbol}`
    },
    {
      accessorKey: 'validUntil',
      header: "Valable jusqu'à",
      enableSorting: true,
      cell: (data) =>
        data.row.original.validUntil ? DateFormatter.DDMMYYYY(data.row.original.validUntil) : '-'
    },
    {
      accessorKey: 'includedInsurance',
      header: "Inclure l'assurance",
      enableSorting: true,
      cell: (data) => (data.row.original.includedInsurance ? 'Oui' : 'Non')
    },
    {
      accessorKey: 'application',
      header: "Mode d'application",
      enableSorting: true,
      cell: (data) =>
        data.row.original.application
          ? promoCodeApplicationMapping[data.row.original.application]
          : '-'
    },
    {
      accessorKey: 'journeys',
      header: 'Séjours associés',
      cell: ({ row }) =>
        row.original.journeys.length === 0 ? (
          <span>-</span>
        ) : (
          <div className={'flex flex-wrap gap-1 py-2'}>
            {row.original.journeys.map((journey, index) => (
              <Badge key={index} text={journey.reference} variant={'white'} />
            ))}
          </div>
        )
    },
    {
      accessorKey: 'productJourneyPlaces',
      header: 'Lieux de séjour associés',
      cell: ({ row }) =>
        row.original.productJourneyPlaces.length === 0 ? (
          <span>-</span>
        ) : (
          <div className={'flex flex-wrap gap-1 py-2'}>
            {row.original.productJourneyPlaces.map((journeyPlace, index) => (
              <Badge key={index} text={journeyPlace.place.name} variant={'white'} />
            ))}
          </div>
        )
    }
  ]

  return (
    <div>
      <UserNavBar title="Codes promo" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<PromoCode>
        ref={tableRef}
        title="Codes promo"
        columns={columns}
        version={'FULL'}
        deleteMessage={{ name: 'code promo', attributeName: 'name' }}
        selectable
        allowSearchParam
        endpoint={API.promoCodes.getAll.url}
        onRowClick={(data, event) =>
          navigateWithEvent(Paths.PROMO_CODE_UPDATE_PAGE.replace(':id', data.id.toString()), event)
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer un code promo',
            fn: () => navigate(Paths.PROMO_CODE_CREATE_PAGE)
          }
        ]}
      />
    </div>
  )
}

export default PromoCodeListPage
